<template>
  <div>
    <div v-if="mediaFile" class="image-spotter w-content">
      <img
        ref="imageEl"
        class="spotter-image"
        :src="mediaFile"
      />
      <div
        class="grid-overlay w-full"
        :style="{
          gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
          gridTemplateRows: `repeat(${gridRows}, 1fr)`
        }"
      >
        <button
          v-for="tileIndex in totalTiles"
          :key="tileIndex - 1"
          type="button"
          class="grid-tile"
          :class="{
            selected: isSelected(tileIndex - 1),
            'completed-state': isCompleted
          }"
          @click="toggleTile(tileIndex - 1)"
          :disabled="isCompleted"
          :aria-pressed="isSelected(tileIndex - 1) ? 'true' : 'false'"
        />
      </div>
    </div>


  </div>
</template>

<script>

export default {
  name: 'GameImageSpotter',
  props: {
    block: {
      type: Object,
      required: true
    },
    gameId: {
      type: [String, Number],
      default: null
    },
    isCompleted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      mediaFile: null,
      gridRows: 3,
      gridCols: 4,
      correctTileIndexes: [],
      selectedTileIndexes: new Set(),
    }
  },
  watch: {
    block: {
      handler(newBlock) {
        if (newBlock?.data?.media) {
          this.mediaFile = newBlock.data.media.preview_url
        }
        if (Number.isInteger(newBlock?.data?.grid_rows) && newBlock.data.grid_rows > 0) {
          this.gridRows = newBlock.data.grid_rows
        }
        if (Number.isInteger(newBlock?.data?.grid_cols) && newBlock.data.grid_cols > 0) {
          this.gridCols = newBlock.data.grid_cols
        }
        if (Array.isArray(newBlock?.data?.correct_tiles)) {
          // Normalize to unique sorted integers within range; validation lenient here
          const size = this.gridRows * this.gridCols
          const normalized = Array.from(new Set(newBlock.data.correct_tiles
            .map(i => parseInt(i, 10))
            .filter(i => Number.isInteger(i) && i >= 0 && i < size)))
          this.correctTileIndexes = normalized
        }
        this.resetSelectionForNewBlock()
        this.applyCompletedState()
      },
      immediate: true
    },
    isCompleted: {
      handler(newValue) {
        if (newValue) {
          this.applyCompletedState()
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.applyCompletedState()
    })
  },
  computed: {
    totalTiles() {
      return this.gridRows * this.gridCols
    },
    isSolved() {
      if (!this.correctTileIndexes || this.correctTileIndexes.length === 0) return false
      // All correct must be selected and no incorrect selected
      const selected = this.selectedTileIndexes
      for (const idx of this.correctTileIndexes) {
        if (!selected.has(idx)) return false
      }
      for (const idx of selected) {
        if (!this.correctTileIndexes.includes(idx)) return false
      }
      return true
    }
  },
  methods: {
    isSelected(index) {
      return this.selectedTileIndexes.has(index)
    },
    toggleTile(index) {
      if (this.isCompleted) {
        console.log('GameImageSpotter: toggleTile: isCompleted', this.isCompleted)
        return
      }
      
      if (this.selectedTileIndexes.has(index)) {
        this.selectedTileIndexes.delete(index)
      } else {
        this.selectedTileIndexes.add(index)
      }
      // Force reactivity for Set in Vue 2 by reassigning
      this.selectedTileIndexes = new Set(this.selectedTileIndexes)
      // Emit completion status based on whether all correct tiles are selected
      const answer = this.isSolved ? 'correct' : 'incorrect'
      console.log('GameImageSpotter: toggleTile: answer', answer)
      this.$emit('complete', { answer })
    },
    
    resetSelectionForNewBlock() {
      this.selectedTileIndexes = new Set()
    },
    
    applyCompletedState() {
      console.log('GameImageSpotter: applyCompletedState: isCompleted', this.isCompleted)

      if (this.isCompleted && Array.isArray(this.correctTileIndexes) && this.correctTileIndexes.length > 0) {
        console.log(`Game ${this.gameId} is completed, showing solved tiles`)

        this.selectedTileIndexes = new Set(this.correctTileIndexes)
        this.$emit('complete', { answer: 'correct' })
      }
    }
  }
}
</script> 

<style scoped>
.image-spotter {
  position: relative;
  display: inline-block;
}

.spotter-image {
  display: block;
  max-width: 100%;
  height: auto;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  display: grid;
  pointer-events: none; /* enable click only on tiles */
}

.grid-tile {
  border: 1px solid rgba(255, 255, 255, 0.8);
  background-color: rgba(0, 0, 0, 0.08);
  cursor: pointer;
  pointer-events: auto;
  padding: 0;
}

.grid-tile:hover {
  background-color: rgba(0, 0, 0, 0.15);
}

.grid-tile.selected {
  background-color: rgba(0, 128, 255, 0.35);
  border-color: rgba(0, 128, 255, 0.9);
}

.grid-tile:disabled {
  cursor: default;
  pointer-events: none;
}

.grid-tile.completed-state.selected {
  background-color: rgba(0, 200, 0, 0.4);
  border-color: rgba(0, 200, 0, 0.9);
}

.status {
  margin-top: 8px;
}
</style>